import { create } from 'zustand';
import { Facture, FactureRequestDTO } from '../types';
import { extractErrorMessage } from '../utils/errorUtils';
import factureService from '../services/factureService';
import { useDashboardStore } from './useDashboardStore';

interface FactureStore {
  factures: Facture[];
  isLoading: boolean;
  error: string | null;
  loadFactures: () => Promise<void>;
  creerFactureAcompte: (devisId: number, pourcentageAcompte: number) => Promise<Facture>;
  creerFactureSolde: (projetId: number, devisId: number) => Promise<Facture>;
  updateFacture: (id: number, data: FactureRequestDTO) => Promise<void>;
  deleteFacture: (id: number) => Promise<void>;
  getFactureById: (id: number) => Facture | undefined;
  getFacturesByDevisId: (devisId: number) => Facture[];
  getFacturesByProjetId: (projetId: number) => Facture[];
  loadFacturesByProjetId: (projetId: number) => Promise<void>;
  markFactureAsPaid: (id: number) => Promise<void>;
  clearFactures: () => void;
}

export const useFactureStore = create<FactureStore>((set, get) => ({
  factures: [],
  isLoading: false,
  error: null,

  // Charger toutes les factures depuis l'API
  loadFactures: async () => {
    set({ isLoading: true, error: null });
    try {
      const factures = await factureService.getAll();
      set({ factures, isLoading: false });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des factures');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Créer une facture d'acompte
  creerFactureAcompte: async (devisId, pourcentageAcompte) => {
    set({ isLoading: true, error: null });
    try {
      const newFacture = await factureService.creerFactureAcompte(devisId, pourcentageAcompte);
      set((state) => ({
        factures: [...state.factures, newFacture],
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();
      return newFacture;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création de la facture d\'acompte');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Créer une facture de solde
  creerFactureSolde: async (projetId, devisId) => {
    set({ isLoading: true, error: null });
    try {
      const newFacture = await factureService.creerFactureSolde(projetId, devisId);
      set((state) => ({
        factures: [...state.factures, newFacture],
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();
      return newFacture;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création de la facture de solde');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Mettre à jour une facture via l'API (uniquement si non émise)
  updateFacture: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const updatedFacture = await factureService.update(id, data);
      set((state) => ({
        factures: state.factures.map((facture) =>
          facture.id === id ? updatedFacture : facture
        ),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour de la facture');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Supprimer une facture via l'API
  deleteFacture: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await factureService.delete(id);
      set((state) => ({
        factures: state.factures.filter((facture) => facture.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la suppression de la facture');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Récupérer une facture par ID (depuis le state local)
  getFactureById: (id) => {
    return get().factures.find((facture) => facture.id === id);
  },

  // Récupérer toutes les factures d'un devis (depuis le state local)
  getFacturesByDevisId: (devisId) => {
    return get().factures.filter((facture) => facture.devisId === devisId);
  },

  // Récupérer toutes les factures d'un projet (depuis le state local)
  getFacturesByProjetId: (projetId) => {
    // On filtre via le devis qui a le projetId correspondant
    // Note: cette fonction est approximative, mieux vaut charger depuis l'API
    return get().factures.filter((facture) => {
      // Ici on ne peut pas vraiment filtrer sans avoir le projetId dans la facture
      // Il faudrait charger les factures du projet depuis l'API
      return true; // Retourne toutes pour l'instant
    });
  },

  // Charger les factures d'un projet depuis l'API
  loadFacturesByProjetId: async (projetId) => {
    set({ isLoading: true, error: null });
    try {
      const factures = await factureService.getByProjetId(projetId);
      // Merge avec les factures existantes
      set((state) => {
        // Filtrer les factures du même projet puis ajouter les nouvelles
        const otherFactures = state.factures.filter(f => {
          return !factures.find(newF => newF.id === f.id);
        });
        return {
          factures: [...otherFactures, ...factures],
          isLoading: false,
        };
      });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des factures du projet');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Marquer une facture comme payée via l'API
  markFactureAsPaid: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const updatedFacture = await factureService.markAsPaid(id);
      set((state) => ({
        factures: state.factures.map((facture) =>
          facture.id === id ? updatedFacture : facture
        ),
        isLoading: false,
      }));
      // Invalider le cache du dashboard
      useDashboardStore.getState().invalidateCache();
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du marquage de la facture comme payée');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Vider toutes les factures (lors du logout)
  clearFactures: () => {
    set({ factures: [], isLoading: false, error: null });
  },
}));
